/**
 * /simple-dashboard — one-glance view for everyday users.
 *
 * Shows the single product with the highest PCF, a Low / Medium / High risk
 * label, and a single "Download Report" button that auto-picks Simple vs.
 * Compliance based on plan tier and risk level.
 */
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import ScenarioSlider from '@/components/ScenarioSlider';
import { pcfAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Download, Loader2, Gauge, Sparkles, AlertTriangle, ShieldCheck, ArrowRight, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const TOUR_STEPS = [
  {
    target: '[data-testid="simple-pcf-value"]',
    title: 'Your Product Carbon Footprint',
    content:
      'This number shows how much CO₂ your product emits per unit produced. It is calculated using the GHG Protocol and ISO 14067 standards. Lower is better.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-testid^="simple-risk-"]',
    title: 'Risk label at a glance',
    content:
      'Low / Medium / High is a quick comparison to typical sector benchmarks. It helps you triage which products to work on first — it is not a regulatory verdict.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="simple-download-report-btn"]',
    title: 'One-click report',
    content:
      'Veridex One picks the right report for you automatically: the 1-page Simple PDF for day-to-day use, or the audit-grade Compliance Report if you are on Pro+ or flagged High risk.',
    placement: 'left',
  },
  {
    target: '[data-testid="simple-to-advanced-link"]',
    title: 'Need deeper control?',
    content:
      'Switch to the Advanced Dashboard whenever you want to manage products, suppliers, invoices, or dive into the CFO / CPO / CSO role dashboards.',
    placement: 'top',
  },
];

// PCF risk thresholds (kg CO2e / unit). Chosen to match typical sector
// benchmarks (steel: 1-3, cement: 0.5-1, aluminium: 5-15).
const RISK = {
  low:    { from: 0,   to: 1.5, label: 'Low',    color: 'bg-green-100 text-green-800 border-green-300',   icon: ShieldCheck },
  medium: { from: 1.5, to: 3.0, label: 'Medium', color: 'bg-amber-100 text-amber-800 border-amber-300',   icon: Gauge },
  high:   { from: 3.0, to: Infinity, label: 'High', color: 'bg-red-100 text-red-800 border-red-300',       icon: AlertTriangle },
};

function riskForPcf(pcf) {
  if (pcf >= RISK.high.from) return 'high';
  if (pcf >= RISK.medium.from) return 'medium';
  return 'low';
}

const COMPLIANCE_TIERS = new Set(['pro', 'advanced', 'enterprise']);

// Pick the right report based on plan + risk.
// Enterprise → always Audit. High risk + compliance-tier → Audit. Else Simple.
function selectReportType(tier, risk, isAdmin) {
  if (isAdmin) return 'audit';
  if (tier === 'enterprise') return 'audit';
  if (risk === 'high' && COMPLIANCE_TIERS.has(tier)) return 'audit';
  return 'simple';
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function SimpleDashboard() {
  const { user } = useAuth();
  const tier = (user?.subscription_tier || 'basic').toLowerCase();
  const isAdmin = user?.role === 'admin';

  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [detail, setDetail] = useState(null); // full PCF detail for ScenarioSlider

  // Fetch PCF detail (contributions breakdown) whenever the selected product
  // changes — used by the ScenarioSlider for an accurate what-if calculation.
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let ok = true;
    pcfAPI.getDetail(selectedId)
      .then((r) => { if (ok) setDetail(r.data); })
      .catch((err) => {
        if (ok) setDetail(null);
        console.error('Failed to load PCF detail', err);
      });
    return () => { ok = false; };
  }, [selectedId]);

  // Listen for the global "start tour" event from HelpCenter.
  useEffect(() => {
    const start = () => setRunTour(true);
    window.addEventListener('veridex:start-tour', start);
    // If the user clicked "Start Tour" from another page, it stored a flag
    // and navigated here — honour it once the data has loaded.
    try {
      if (sessionStorage.getItem('veridex:tour-pending') === '1') {
        sessionStorage.removeItem('veridex:tour-pending');
        setTimeout(() => setRunTour(true), 800);
      }
    } catch { /* noop */ }
    return () => window.removeEventListener('veridex:start-tour', start);
  }, []);

  // Auto-start the tour on a user's very first visit (persistent flag in
  // localStorage, scoped per-user so different accounts on the same browser
  // each get their own onboarding). Skipped if they've seen it before or
  // there's no product to point the tooltips at.
  const tourDoneKey = user?.id ? `veridex:tour-completed:${user.id}` : 'veridex:tour-completed';
  useEffect(() => {
    if (loading || !products.length) return;
    try {
      if (!localStorage.getItem(tourDoneKey)) {
        const t = setTimeout(() => setRunTour(true), 1500);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, [loading, products.length, tourDoneKey]);

  const markTourDone = () => {
    setRunTour(false);
    try { localStorage.setItem(tourDoneKey, '1'); } catch { /* noop */ }
  };

  const handleTourCallback = (data) => {
    const { status, type, action } = data;
    // Terminal statuses — user finished the tour or explicitly skipped it.
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      markTourDone();
      return;
    }
    // User clicked the X (close) button on any step, or the tour errored.
    if (action === ACTIONS.CLOSE || status === STATUS.ERROR) {
      markTourDone();
      return;
    }
    // A step target could not be found — bail out gracefully rather than
    // leaving the modal spinning forever waiting for a DOM node.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      markTourDone();
    }
  };

  useEffect(() => {
    let ok = true;
    pcfAPI.getProducts()
      .then((r) => {
        if (!ok) return;
        const sorted = [...(r.data || [])].sort(
          (a, b) => (Number(b.pcf) || 0) - (Number(a.pcf) || 0)
        );
        setProducts(sorted);
        if (sorted.length) setSelectedId(sorted[0].id);
      })
      .catch(() => toast.error('Could not load products'))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, []);

  const selected = useMemo(
    () => products.find((p) => p.id === selectedId) || null,
    [products, selectedId]
  );
  const pcfValue = selected ? Number(selected.pcf) || 0 : 0;
  const riskKey = selected ? riskForPcf(pcfValue) : 'low';
  const riskMeta = RISK[riskKey];
  const RiskIcon = riskMeta.icon;
  const reportType = selectReportType(tier, riskKey, isAdmin);
  const isApproved = selected?.review_status === 'approved';

  const handleDownload = async () => {
    if (!selectedId) return;
    if (!isApproved && !isAdmin) {
      toast.error('This PCF must be approved by a human reviewer before it can be exported.');
      return;
    }
    setDownloading(true);
    try {
      const useAudit = reportType === 'audit';
      const res = useAudit
        ? await pcfAPI.exportAuditPdf(selectedId)
        : await pcfAPI.exportPdf(selectedId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const safe = (selected?.name || 'product').replace(/\s+/g, '_').slice(0, 40);
      downloadBlob(blob, useAudit ? `Veridex_Compliance_PCF_${safe}.pdf` : `Veridex_PCF_${safe}.pdf`);
      toast.success(useAudit ? 'Compliance report downloaded' : 'Report downloaded');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail;
      // Graceful fallback: compliance gate returned 402 → try simple
      if (reportType === 'audit' && status === 402) {
        try {
          const r2 = await pcfAPI.exportPdf(selectedId);
          const blob = new Blob([r2.data], { type: 'application/pdf' });
          const safe = (selected?.name || 'product').replace(/\s+/g, '_').slice(0, 40);
          downloadBlob(blob, `Veridex_PCF_${safe}.pdf`);
          toast.info('Audit report requires Pro+ — downloaded Simple PDF instead.');
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || 'Download failed.');
        }
      } else {
        toast.error(msg || 'Download failed.');
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="simple-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!products.length) {
    return (
      <Card className="p-10 text-center" data-testid="simple-dashboard-empty">
        <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-heading font-semibold mb-1">No products yet</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Add a product to see your PCF, risk level, and report.
        </p>
        <Link to="/products">
          <Button className="bg-primary hover:bg-primary/90" data-testid="simple-add-product-btn">
            Add your first product <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="simple-dashboard">
      <Joyride
        steps={TOUR_STEPS}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        disableOverlayClose
        callback={handleTourCallback}
        locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
        styles={{
          options: {
            primaryColor: '#1E3A8A',
            zIndex: 10000,
          },
          tooltipContainer: { textAlign: 'left' },
        }}
      />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Your carbon snapshot</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Showing the product with your highest footprint. One click to download an audit-ready report.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRunTour(true)}
            data-testid="simple-start-tour-btn"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> Take the tour
          </Button>

          {products.length > 1 && (
            <div className="w-48 sm:w-56" data-testid="simple-product-picker">
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="simple-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — {(Number(p.pcf) || 0).toFixed(2)} kg
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <Card className="p-8 sm:p-10" data-testid="simple-dashboard-card">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              {selected?.name || '—'}
            </div>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="text-6xl sm:text-7xl font-heading font-bold text-primary tracking-tight" data-testid="simple-pcf-value">
                {pcfValue.toFixed(2)}
              </span>
              <span className="text-sm text-muted-foreground">kg CO₂e / unit</span>
            </div>

            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${riskMeta.color}`}
              data-testid={`simple-risk-${riskKey}`}
            >
              <RiskIcon className="w-4 h-4" />
              Risk: {riskMeta.label}
            </div>

            {!isApproved && !isAdmin && (
              <div className="mt-4 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Export is locked until an editor or admin approves this PCF on the{' '}
                <Link to="/pcf" className="underline font-medium">PCF page</Link>.
              </div>
            )}
          </div>

          <div className="md:text-right space-y-3">
            <Button
              size="lg"
              onClick={handleDownload}
              disabled={downloading || (!isApproved && !isAdmin)}
              className="w-full md:w-auto bg-primary hover:bg-primary/90"
              data-testid="simple-download-report-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? 'Preparing…' : 'Download Report'}
            </Button>
            <div className="text-xs text-muted-foreground" data-testid="simple-report-type">
              Auto-selected: <span className="font-medium text-foreground">{reportType === 'audit' ? 'Compliance Report' : 'Simple PDF'}</span>
              {reportType === 'audit'
                ? ' · audit-grade, Executive Summary + dynamic data quality'
                : ' · 1-page PCF summary'}
            </div>
            <div className="text-xs text-muted-foreground">
              Plan: <span className="font-medium text-foreground uppercase">{tier}</span> · need deep control?{' '}
              <Link to="/" className="text-primary hover:underline" data-testid="simple-to-advanced-link">Switch to Advanced Dashboard →</Link>
            </div>
          </div>
        </div>
      </Card>

      <ScenarioSlider detail={detail} />

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="simple-tip-pcf">
          <div className="text-xs text-muted-foreground mb-1">What is this number?</div>
          <div className="text-sm font-medium">
            kg of CO₂ equivalent emitted per unit of output. Lower is better.
          </div>
        </Card>
        <Card className="p-5" data-testid="simple-tip-risk">
          <div className="text-xs text-muted-foreground mb-1">Risk level</div>
          <div className="text-sm font-medium">
            Low &lt; 1.5 · Medium 1.5 – 3 · High &gt; 3 (vs. typical sector benchmarks)
          </div>
        </Card>
        <Card className="p-5" data-testid="simple-tip-report">
          <div className="text-xs text-muted-foreground mb-1">Report type</div>
          <div className="text-sm font-medium">
            Enterprise &amp; High-risk on Pro+ get the Compliance Report automatically.
          </div>
        </Card>
      </div>
    </div>
  );
}
